import { createSelector } from "@reduxjs/toolkit";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MultiSelect } from "react-multi-select-component";
import { toast, ToastContainer } from "react-toastify";
import { useCountryArr } from "../../../hooks/useCountryArr";
import {
  useDeleteDistributionListMutation,
  useGetAllDistributionListsQuery,
  useNewDistributionListMutation,
  useUpdateDistributionListMutation,
} from "../../../services/API/EmailDistributionAPI";
import { useGetUsersQuery } from "../../../services/API/UsersAPI";
import CountriesSelect from "./CountriesSelect";
import "./EmailDistribution.scss";

function EmailDistribution() {
  const { t } = useTranslation(["adminNotificationConfig"]);
  const countryArr = useCountryArr();

  // https://redux.js.org/tutorials/essentials/part-8-rtk-query-advanced#selecting-values-from-results
  const selectUnusedEmails = useMemo(() => {
    const emptyArray = [];
    return createSelector(
      (res) => res.data,
      (res, param) => param,
      (queryResult, distrbList) => {
        return (
          queryResult?.filter(
            (adminUser) =>
              !distrbList?.some((distrbListElement) => {
                return adminUser?.email === distrbListElement?.admin?.email;
              })
          ) ?? emptyArray
        );
      }
    );
  }, []);

  const { data: emailDistributionLists } = useGetAllDistributionListsQuery();
  const { filteredAdmins } = useGetUsersQuery(
    { roles: ["Approver"] },
    {
      selectFromResult: (result) => ({
        ...result,
        filteredAdmins: selectUnusedEmails(result, emailDistributionLists),
      }),
    }
  );
  const [update] = useUpdateDistributionListMutation();
  const [deleteList] = useDeleteDistributionListMutation();
  const [create] = useNewDistributionListMutation();

  const [createNewCountries, setCreateNewCountries] = useState([]);

  let defaultSelection = "default";
  const [createNewEmail, setCreateNewEmail] = useState(defaultSelection);

  const handleCreateNew = async (e) => {
    e.preventDefault();

    if (createNewEmail === defaultSelection) {
      toast.error(t("error.emptyEmail", { ns: "adminNotificationConfig" }));
      return;
    }

    let newDistributionAreaIsoArray = createNewCountries.map((element) => {
      return element.value;
    });

    try {
      await create({
        data: {
          distributionArea: newDistributionAreaIsoArray,
          email: createNewEmail,
        },
      })
        .unwrap()
        .then((payload) => {
          toast.success(t("success.new", { ns: "adminNotificationConfig" }));
          setCreateNewCountries([]);
          setCreateNewEmail(defaultSelection);
        })
        .catch((error) => {
          if (error.status === 400) {
            return toast.error(t("error.duplicateMail", { ns: "adminNotificationConfig" }));
          }
          throw new Error("Unhandled response type!");
        });
    } catch (error) {
      console.error(error);
      toast.error(t("error.internalError", { ns: "adminNotificationConfig" }));
    }
  };

  const handleUpdate = async (id, newAreaData) => {
    if (newAreaData.length === 0 && emailDistributionLists.length === 1) {
      toast.warning(t("warning.lastElementUpdate", { ns: "adminNotificationConfig" }));
      return { success: false };
    }

    try {
      await update({
        id: id,
        data: {
          distributionArea: newAreaData,
        },
      })
        .unwrap()
        .then((payload) => {
          toast.success(t("success.updated", { ns: "adminNotificationConfig" }));
        });

      return { success: true };
    } catch (error) {
      console.error(error);
      toast.error(t("error.internalError", { ns: "adminNotificationConfig" }));
      return { success: false };
    }
  };

  const handleDelete = async (element) => {
    try {
      if (emailDistributionLists.length === 1) {
        toast.error(t("error.lastElementremoval", { ns: "adminNotificationConfig" }));
        return;
      }

      await deleteList({ id: element._id })
        .unwrap()
        .then((payload) => {
          toast.success(t("success.removed", { ns: "adminNotificationConfig" }));
        });
    } catch (error) {
      console.error(error);
      toast.error(t("error.internalError", { ns: "adminNotificationConfig" }));
    }
  };

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss={false}
        draggable
        pauseOnHover
        theme="light"
      />
      <div className="emailDistributionLists">
        <div className="row d-flex justify-content-center">
          <div className="col-md-4">
            <div className="mb-3">
              <label htmlFor="floatingSelect" className="form-label">
                {t("adminNotification.emails", {
                  ns: "adminNotificationConfig",
                })}
              </label>
              <select
                className="form-select"
                id="floatingSelect"
                value={createNewEmail}
                onChange={(e) => setCreateNewEmail(e.target.value)}
              >
                <option key={0} value={defaultSelection}>
                  {t("adminNotification.select", {
                    ns: "adminNotificationConfig",
                  })}
                </option>
                {filteredAdmins?.map((ele, index) => {
                  return (
                    <option key={index} value={ele.email}>
                      {ele?.email}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
          <div className="col-md-4 align-self-center">
            <div className="mb-3">
              <label htmlFor="inputCountriesSelect" className="form-label">
                {t("adminNotification.countries", {
                  ns: "adminNotificationConfig",
                })}
              </label>
              <MultiSelect
                id="inputCountriesSelect"
                options={countryArr}
                value={createNewCountries}
                onChange={setCreateNewCountries}
                overrideStrings={{
                  selectSomeItems: t("countrySelect.selectSomeItems"),
                  selectAll: t("countrySelect.selectAll"),
                  search: t("countrySelect.search"),
                }}
              />
            </div>
          </div>
          <div className="col-auto align-self-center">
            <button type="submit" className="btn btn-primary mt-3" onClick={handleCreateNew}>
              {t("adminNotification.createNew", {
                ns: "adminNotificationConfig",
              })}
            </button>
          </div>
        </div>
        <br />
        <div className="row d-flex justify-content-center">
          <div className="col-8">
            <span className="emaillist">
              {emailDistributionLists?.map((element) => (
                <li key={element._id} className="listrow">
                  <div className="row d-flex justify-content-evenly align-items-center">
                    <div className="col-sm-4 text-break">{element?.admin?.email}</div>
                    <div className="col-sm-5">
                      <CountriesSelect
                        isoCountries={element.distributionArea}
                        handleChange={(newAreaData) => handleUpdate(element._id, newAreaData)}
                      />
                    </div>
                    <div className="col d-flex justify-content-end">
                      <div
                        data-testid="distribution-delete"
                        className="btn-close"
                        aria-label="Close"
                        onClick={() => handleDelete(element)}
                      />
                    </div>
                  </div>
                </li>
              ))}
            </span>
          </div>
        </div>
      </div>
    </>
  );
}

export default EmailDistribution;
